/**
 * JatakaApiTypes - Complete typing for Jataka API response
 *
 * Based on actual API response structure from localhost:8027/api/v1/jataka
 * Server provides 100% validated and normalized data - no frontend validation needed
 */

/**
 * Individual planet/graha position from API
 */
export interface ApiGrahaPosition {
    name: string;                    // Sanskrit name: "Surya", "Chandra", etc.
    short_name: string;              // 2-letter code: "Sy", "Ch", "Ma", etc.
    longitude: number;               // Decimal degrees: 112.158686
    longitude_dms: string;           // DMS format: "112° 09' 31.27\""
    latitude: number;                // Decimal latitude
    speed: number;                   // Daily motion in degrees
    is_retrograde: boolean;          // Retrograde status
    rasi_number: number;             // 1-12 rasi number
    rasi_name: string;               // Sanskrit rasi: "Karka", "Simha", etc.
    nakshatra_number: number;        // 1-27 nakshatra number
    nakshatra_name: string;          // Sanskrit nakshatra: "Ashlesha", etc.
    nakshatra_pada: number;          // 1-4 pada number
    navamsa_rasi: string;            // Navamsa rasi in Sanskrit
    position: string;                // Unified format: "rasi_idx|degrees_in_rasi|longitude|speed"
    muhurta_num: number;             // 1-30 muhurta sector number
    muhurta_graha: string;           // Muhurta ruling graha in Sanskrit
    muhurta_lord: string;            // Muhurta lord name
}

/**
 * Graha positions section
 */
export interface ApiGrahaPositions {
    planets: ApiGrahaPosition[];     // Array of all planets (excluding Lagna)
    lagna?: ApiGrahaPosition;        // Lagna position (if present in planets array, extract separately)
}

/**
 * Basic chart information
 */
export interface ApiBasicInfo {
    date: string;                    // "August 08, 2025"
    time: string;                    // "00:42:57"
    datetime_iso: string;            // "2026-01-03T21:31:05.123456+02:00" - full precision ISO 8601
    timezone: string;                // "UTC+02:00" or "UTC-05:30"
    location: string;                // "34 E 32' 59\", 48 N 35' 24\""
    place_name?: string;             // "Kamenske, Ukraine" - optional in spread operations
    altitude: string;                // "0.00 meters"
    latitudeValue: number;           // 48.59
    longitudeValue: number;          // 34.55
    chart_name?: string;             // Optional chart name from server
}

/**
 * Astronomical data - all pre-calculated by server
 */
export interface ApiAstronomical {
    sunrise: string;                     // "05:26:08" - HH:MM:SS format
    sunrise_surya_longitude: number;     // 214.644258 - decimal degrees (0-360)
    sunrise_surya_longitude_dms: string; // "214° 38' 39.33\"" - DMS format for display
    sunset: string;                      // "20:08:07" - HH:MM:SS format
    next_sunrise?: string;               // "06:47:45" - next day sunrise (HH:MM:SS)
    day_duration_sec?: number;           // 39390 - day duration in seconds (sunset - sunrise)
    night_duration_sec?: number;         // 46965 - night duration in seconds (next_sunrise - sunset)
    janma_ghatis: string;                // "48.18" - birth ghatis
    ayanamsa: string;                    // "23-28-49.79" - ayanamsa value
    ayanamsa_system: string;             // "Rohini Paksha" - ayanamsa system
    sidereal_time: string;               // "18:49:36.27" - local sidereal time
    julian_day: number;                  // 2460895.4048263887 - Julian day number
}

/**
 * Muhurta details from detailed_panchanga
 */
export interface ApiMuhurtaDetails {
    number: number;                  // 1-30 muhurta number
    name: string;                    // Sanskrit name: "Aditya"
    lord: string;                    // "Lord of blessings" - descriptive name
    graha_ruler: string;             // Sanskrit ruler: "Surya"
    current_ghati: string;           // Ghati info: "('Indumukha', 'Pravina')"
    completion: number;              // 0-100 percentage completion
    elapsed: number;                 // Time elapsed in this muhurta
    chakra_longitude: string;        // "40° 35' 30.08\"" - ML position in DMS
}

/**
 * Masa (month) details
 */
export interface ApiMasaDetails {
    number: number;                  // Month number
    name: string;                    // Sanskrit month name: "Shravana"
    alternative: string;             // Alternative name: "Nabha"
    ruling_deity: string;            // "Indra"
    description: string;             // Descriptive text
    season: string;                  // "Varsha" - season
    days_in_masa: number;            // Days in this masa
    completion: number;              // Percentage completion
    lunar_month_days: number;        // Total lunar month days
    new_moon: string;                // ISO date string with timezone
    next_new_moon: string;           // ISO date string with timezone
    days_to_next: number;            // Days to next new moon
}

/**
 * Tithi details
 */
export interface ApiTithiDetails {
    number: number;                  // 1-15 tithi number
    name: string;                    // Sanskrit tithi name
    paksha: string;                  // "Shukla" or "Krishna"
    paksha_tithi: number;            // Tithi within paksha
    ruling_planet: string;           // Sanskrit planet name
    patron_deity?: string;           // Patron deity
    kind?: string;                   // Tithi kind (Purna, Rikta, etc.)
    nature?: string;                 // Tithi nature (legacy)
    completion: number;              // Percentage completion
    remaining?: number;              // Percentage/degrees remaining
    end_time?: string;               // End time with timezone
}

/**
 * Nakshatra details from detailed_panchanga
 */
export interface ApiNakshatraDetails {
    number: number;                  // 1-27 nakshatra number
    name: string;                    // Sanskrit name
    ruling_planet: string;           // Ruling graha
    completion: number;              // Percentage completion
    remaining: number;               // Remaining degrees
    remaining_arc_minutes: number;   // Remaining in arc minutes
}

/**
 * Karana details from detailed_panchanga
 */
export interface ApiKaranaDetails {
    id: number;                      // Karana ID
    name: string;                    // Sanskrit name
    type: string;                    // "Sthira" or "Chara"
    ruling_planet: string;           // Ruling graha
    completion_percentage: number;   // Percentage completion
    remaining_degrees: number;       // Remaining degrees
}

/**
 * Yoga details from detailed_panchanga
 */
export interface ApiYogaDetails {
    number: number;                  // 1-27 yoga number
    name: string;                    // Sanskrit name
    ruling_planet: string;           // Ruling graha
    completion: number;              // Percentage completion
    remaining: number;               // Remaining degrees
}

/**
 * Vara details from detailed_panchanga
 */
export interface ApiVaraDetails {
    number: number;                  // 1-7 day number
    sanskrit: string;                // Sanskrit name
    english: string;                 // English name
    ruling_planet: string;           // Ruling graha
    planet_code: string;             // Two-letter code
}

/**
 * Ghati details from detailed_panchanga
 */
export interface ApiGhatiDetails {
    number: number;                  // 1-60 ghati number
    name: string;                    // Sanskrit name
    lord: string;                    // Ghati lord description
    parent_muhurta: string;          // Parent muhurta name
    janma_ghati: number;             // Birth ghati value
    traditional: string;             // Traditional format "HH:MM:SS"
    format: string;                  // Format "Xg Yp Zl"
}

/**
 * Hora details from detailed_panchanga
 */
export interface ApiHoraDetails {
    current_planet: string;          // Direct planet name: "Surya", "Chandra", etc.
    hour_number: number;             // 1-24 hour number
    period: string;                  // "Day" or "Night"
    hora_duration_sec?: number;      // Duration of current hora in seconds
    hora_elapsed_sec?: number;       // Seconds elapsed since current hora started
    hora_start?: string;             // "19:15:30" - start time of current hora (HH:MM:SS)
    hora_end?: string;               // "20:10:15" - end time of current hora (HH:MM:SS)
}

/**
 * Detailed panchanga data
 */
export interface ApiDetailedPanchanga {
    samvat_details: {
        id: number;
        name: string;                // "Vishvavasu"
        year_start: string;          // ISO date string
        jupiter_pos: number;         // Jupiter position
        source: string;              // "Primary calculation"
    };
    masa_details: ApiMasaDetails;
    tithi_details: ApiTithiDetails;
    nakshatra_details: ApiNakshatraDetails;
    karana_details: ApiKaranaDetails;
    yoga_details: ApiYogaDetails;
    vara_details: ApiVaraDetails;
    muhurta_details: ApiMuhurtaDetails;
    hora_details: ApiHoraDetails;
    ghati_details: ApiGhatiDetails;
}

/**
 * Basic panchanga summary
 */
export interface ApiPanchanga {
    lunar_year_month: string;        // "Vishvavasu - Shravana"
    tithi: string;                   // "Chaturdashi (Sk) - Rikta (46.66% left)"
    weekday: string;                 // "Thursday (Gu)"
    nakshatra: string;               // "Uttara Ashadha (Sy) (40.81% left)"
    yoga: string;                    // "Ayushman (Ke) (99.61% left)"
    karana: string;                  // "Vanija (Sk) (93.32% left)"
    hora_lord: string;               // "Chandra (20/24)" - current hora
    muhurta: string;                 // "Aditya (25/30) - Lord of blessings (89.97% left)"
    ghati: string;                   // "48.1800 (48:12:01) - Indumukha (49/60)"
}

/**
 * Calculation metadata
 */
export interface ApiCalculationMetadata {
    calculation_time?: string;       // Server calculation timestamp
    ephemeris_version?: string;      // Swiss Ephemeris version
    ayanamsa_used?: string;          // Ayanamsa system used
    timezone_offset?: number;        // Hours from GMT (e.g., 3.0, -5.0, -10.5)
    [key: string]: any;              // Allow additional metadata
}

/**
 * Complete Jataka API Response
 * All data is pre-validated and normalized by server
 */
export interface JatakaApiResponse {
    basic_info: ApiBasicInfo;
    graha_positions: ApiGrahaPositions;
    astronomical: ApiAstronomical;
    panchanga: ApiPanchanga;
    detailed_panchanga: ApiDetailedPanchanga;
    calculation_metadata?: ApiCalculationMetadata;
}

/**
 * Type guard to check if response is valid JatakaApiResponse
 */
export function isValidJatakaApiResponse(data: any): data is JatakaApiResponse {
    return (
        data &&
        typeof data === 'object' &&
        data.graha_positions &&
        data.astronomical &&
        data.astronomical.sunrise_surya_longitude &&
        Array.isArray(data.graha_positions.planets)
    );
}

/**
 * Enhanced validation with detailed error reporting
 */
export function validateJatakaApiResponse(data: any): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    dataType: 'jataka_api' | 'legacy_chart' | 'unknown';
} {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!data || typeof data !== 'object') {
        return {
            isValid: false,
            errors: ['Data is null or not an object'],
            warnings: [],
            dataType: 'unknown'
        };
    }

    // Check if this looks like legacy chart data
    if (data.planets && Array.isArray(data.planets) && !data.graha_positions) {
        return {
            isValid: false,
            errors: ['Legacy chart data detected - use legacy handling'],
            warnings: ['This appears to be saved chart data, not fresh API response'],
            dataType: 'legacy_chart'
        };
    }

    // Validate Jataka API structure
    if (!data.graha_positions) {
        errors.push('Missing graha_positions');
    } else if (!Array.isArray(data.graha_positions.planets)) {
        errors.push('graha_positions.planets is not an array');
    }

    if (!data.astronomical) {
        errors.push('Missing astronomical data');
    } else if (!data.astronomical.sunrise_surya_longitude) {
        errors.push('Missing astronomical.sunrise_surya_longitude');
    }

    if (!data.basic_info) {
        warnings.push('Missing basic_info');
    }

    if (!data.panchanga) {
        warnings.push('Missing panchanga');
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        dataType: errors.length === 0 ? 'jataka_api' : 'unknown'
    };
}

/**
 * Extract hora data from API using both panchanga and detailed_panchanga
 */
export function extractHoraFromApi(
    panchanga: ApiPanchanga,
    detailedPanchanga?: ApiDetailedPanchanga
): { graha: string | null; lord: string; currentPlanet?: string } | null {
    if (!panchanga.hora_lord) return null;

    // Parse "Chandra (20/24)" format for graha
    const match = panchanga.hora_lord.match(/^(\w+)\s*\(/);
    const graha = match ? match[1] : null;

    // Get current_planet directly from detailed_panchanga.hora_details
    const currentPlanet = detailedPanchanga?.hora_details?.current_planet;

    return {
        graha,
        lord: panchanga.hora_lord,
        currentPlanet
    };
}